import { useCallback, useEffect, useState } from 'react'
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@/Components/ui/pagination'
import { useLocation, useNavigate } from 'react-router-dom'
import Constent from '@/utils/constent'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select'

export function PaginationWithLinks({
    totalCount,
    pageSearchParam,
    startSerial = 0,
    endSerial = 0,
    pageSizeRange = [],
    onPageCountChange,
    showCountLable = false,
}) {
    const location = useLocation()
    const navigate = useNavigate()
    const searchParams = new URLSearchParams(location.search)
    const newSearchParams = new URLSearchParams(searchParams)

    // Parse and sanitize page
    const page = parseInt(searchParams.get('page') || '1', 10)

    // Parse and sanitize limit (page size)
    const rawLimit = searchParams.get('limit')
    const initialPageSize = rawLimit && rawLimit !== 'undefined' ? parseInt(rawLimit, 10) : Constent.PAGINATION_SIZE

    const [currentPageSize, setCurrentPageSize] = useState(initialPageSize)
    const totalPageCount = Math.ceil(totalCount / currentPageSize)
    const buildLink = useCallback(
        (newPage) => {
            const key = pageSearchParam || 'page'
            newSearchParams.set(key, String(newPage))
            return `${location.pathname}?${newSearchParams.toString()}`
        },
        [searchParams, location.pathname, pageSearchParam]
    )

    const renderPageNumbers = () => {
        const items = []
        const maxVisiblePages = 5

        if (totalPageCount <= maxVisiblePages) {
            for (let i = 1; i <= totalPageCount; i++) {
                items.push(
                    <PaginationItem key={i}>
                        <PaginationLink to={buildLink(i)} isActive={page === i}>
                            {i}
                        </PaginationLink>
                    </PaginationItem>
                )
            }
        } else {
            items.push(
                <PaginationItem key={1}>
                    <PaginationLink to={buildLink(1)} isActive={page === 1}>
                        1
                    </PaginationLink>
                </PaginationItem>
            )

            if (page > 3) {
                items.push(
                    <PaginationItem key="ellipsis-start">
                        <PaginationEllipsis />
                    </PaginationItem>
                )
            }

            const start = Math.max(2, page - 1)
            const end = Math.min(totalPageCount - 1, page + 1)

            for (let i = start; i <= end; i++) {
                items.push(
                    <PaginationItem key={i}>
                        <PaginationLink to={buildLink(i)} isActive={page === i}>
                            {i}
                        </PaginationLink>
                    </PaginationItem>
                )
            }

            if (page < totalPageCount - 2) {
                items.push(
                    <PaginationItem key="ellipsis-end">
                        <PaginationEllipsis />
                    </PaginationItem>
                )
            }

            items.push(
                <PaginationItem key={totalPageCount}>
                    <PaginationLink to={buildLink(totalPageCount)} isActive={page === totalPageCount}>
                        {totalPageCount}
                    </PaginationLink>
                </PaginationItem>
            )
        }

        return items
    }

    return (
        <div className={`flex flex-row items-center gap-3 w-full my-3 ${showCountLable ? 'justify-between' : 'justify-end'}`}>
            {showCountLable && (
                <p className="text-[14px]">
                    Showing {startSerial} to {endSerial} of {totalCount} record
                </p>
            )}
            <div className="flex flex-row justify-between items-center">
                {pageSizeRange.length > 0 && (
                    <div className="flex items-center me-4">
                        <span className="font-light text-nowrap me-2 text-[14px]">Page Size:</span>
                        <Select
                            value={String(currentPageSize)}
                            onValueChange={(v) => {
                                const valueAsNumber = parseInt(v, 10)
                                setCurrentPageSize(valueAsNumber)
                                onPageCountChange(valueAsNumber)
                                newSearchParams.set('limit', valueAsNumber)
                                newSearchParams.set('page', 1)
                                navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true })
                            }}
                        >
                            <SelectTrigger className="w-full text-black">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {pageSizeRange.map((pageSize) => (
                                    <SelectItem key={pageSize} value={String(pageSize)}>
                                        {pageSize}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
                <Pagination className="justify-end">
                    <PaginationContent className="max-sm:gap-0">
                        <PaginationItem>
                            <PaginationPrevious
                                to={buildLink(Math.max(page - 1, 1))}
                                aria-disabled={page === 1}
                                tabIndex={page === 1 ? -1 : undefined}
                                className={page === 1 ? 'pointer-events-none opacity-50' : undefined}
                            />
                        </PaginationItem>
                        {renderPageNumbers()}
                        <PaginationItem>
                            <PaginationNext
                                to={buildLink(Math.min(page + 1, totalPageCount))}
                                aria-disabled={page === totalPageCount || totalPageCount === 0}
                                tabIndex={page === totalPageCount ? -1 : undefined}
                                className={page === totalPageCount || totalPageCount === 0 ? 'pointer-events-none opacity-50' : undefined}
                            />
                        </PaginationItem>
                    </PaginationContent>
                </Pagination>
            </div>
        </div>
    )
}
